import React, { useCallback, useEffect, useState } from 'react';
import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { AssessmentOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { examTypeService } from '../../services/examTypeService';
import { ExamType } from '../../types';
import SettingsList from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';

/** Every exam type the school has — a settings list is never paged. */
const ALL = 500;

const ExamTypesSettings: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_exam_types');

  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ExamType | null>(null);
  const [form, setForm] = useState({ name: '', description: '', countsInReportsByDefault: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await examTypeService.getExamTypes({ limit: ALL });
      setExamTypes(res.data?.examTypes ?? []);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load exam types', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const edit = (t: ExamType) => {
    setEditing(t);
    setForm({ name: t.name, description: t.description ?? '', countsInReportsByDefault: t.countsInReportsByDefault !== false });
  };

  const save = async () => {
    if (!editing || !form.name.trim()) return;
    setSaving(true);
    try {
      await examTypeService.updateExamType(editing._id, { ...form, name: form.name.trim() });
      enqueueSnackbar('Exam type saved', { variant: 'success' });
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this exam type', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsList
        rows={examTypes}
        loading={loading}
        rowKey={t => t._id}
        name={t => t.name}
        subtitle={t => [t.description, t.countsInReportsByDefault === false ? 'Not counted in reports' : ''].filter(Boolean).join(' · ') || undefined}
        searchText={t => `${t.name} ${t.description ?? ''}`}
        canWrite={writable}
        onEdit={edit}
        remove={{
          run: t => examTypeService.deleteExamType(t._id),
          noun: 'exam type',
          consequence: () => 'It will no longer be offered when scheduling exams. Exams already held keep their type and results.',
        }}
        quickAdd={{
          noun: { one: 'exam type', many: 'exam types' },
          placeholder: 'Add exam types, e.g. Monthly Test, Midterm, Final Term',
          create: name => examTypeService.createExamType({ name }),
          presets: [
            { label: 'Term exams', names: ['Monthly Test', 'Midterm', 'Final Term'] },
            { label: 'Board style', names: ['Preliminary', 'Send-up', 'Annual'] },
          ],
        }}
        reload={load}
        empty={{
          icon: <AssessmentOutlined />,
          title: 'No exam types yet',
          description: 'Exam types are the kinds of exams you schedule. Add them above — several at once if you like.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title="Edit exam type"
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveDisabled={!form.name.trim()}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          error={!form.name.trim()}
          helperText={!form.name.trim() ? 'An exam type needs a name' : ' '}
          inputProps={{ maxLength: 100 }}
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Optional"
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={2}
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={form.countsInReportsByDefault} onChange={e => setForm(f => ({ ...f, countsInReportsByDefault: e.target.checked }))} />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>Counts in reports</Typography>
              <Typography variant="caption" color="text.secondary">
                New exams of this type appear on transcripts and mark sheets and count in averages. Turn off for quizzes and practice tests — each exam can still be changed.
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0, gap: 1 }}
        />
      </SettingsDialog>
    </>
  );
};

export default ExamTypesSettings;
